import React, { useState, useEffect, ChangeEvent, useRef } from "react";
import "./component.css";
import { useNavigate } from "react-router-dom";
import { makeAuthenticatedPostForFileResponse } from "../utils/fetchUtils";
import { PARSE_LINKEDIN, PARSE_RESUME } from "../utils/constants";
import uploadImg from "../assets/upload-img.svg";
import or from "../assets/or.svg";

interface FormData {
  file: File | null;
  tag: string,
  fileName: string,
  linkedIn: string
}

const OpportunityForm = (props:any) => {
  const navigate = useNavigate();

  const inputRef = useRef<HTMLInputElement>(null);
  const isMountedRef = useRef(true);
  
  function handleSkip(event:any): void {
    event.preventDefault();
    navigate("/opportunity/chatbot", { state: { useResumeContext: false } });
  }
  // react states
  const [formData, setFormData] = useState<FormData>({
    file: null,
    tag: "",
    fileName: '',
    linkedIn : ''
  });
  const [isDirty, setIsDirty] = useState(true);
  const [fileName, setFileName] = useState("");
  // const [linkedIn,setLinkedIn] = useState("");
  const [selectedOption, setSelectedOption] = useState("");
  const [error, setError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [isParsing, setIsParsing] = useState(false);

  //drag to upload
      const [dragging, setDragging] = useState(false);
    
      // Handlers for drag events
      const handleDragEnter = (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        if (isParsing) return;
        setDragging(true);
      };
    
      const handleDragLeave = (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(false);
      };
    
      const handleDragOver = (e: any) => {
        e.preventDefault();
        e.stopPropagation();
      };
    
      const handleDrop = (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        if (isParsing) return;
        setDragging(false);
    
        if (e.dataTransfer.files.length > 1) {
          alert("Please drag and drop only one file at a time.");
          return;
        }
    
        const droppedFile = e.dataTransfer.files[0]; // Get the first file
        if (droppedFile) {
          setFileName(droppedFile.name);
          setFormData({
            ...formData,
            file: droppedFile,
            fileName: droppedFile.name,
          });
        }
      };

  const handleOptionChange = (event: any) => {
    setSelectedOption(event.target.value);
    handleChange(event);
  };

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {

    const regex = /^https:\/\/www\.linkedin\.com\/in\/[a-zA-Z0-9-]+\/?$/;
    if (event.target.name === 'linkedIn') {
      if (event.target.value === '') {
        setError(''); // Clear error if the value is empty
      } else if (!regex.test(event.target.value)) {
        setError('Please enter a valid LinkedIn profile URL.');
      } else {
        setError(''); // Clear error if the value is valid
      }
    }
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    const file = event.target.files ? event.target.files[0] : null;
    if (file) {
      setFileName(file.name);
      setFormData({
        ...formData,
        file: files ? files[0] : null,
        fileName : file.name
      });
    } else {
      setFileName(event.target.value);
      setFormData({
        ...formData,
        fileName: event.target.value,
      });
    }
  };
  

  const handleSubmit = async (event: any) => {
    event.preventDefault();
    if (isParsing) return;

    if (!formData.file && props.hasSavedResume) {
      if (props.for === 'job') {
        navigate('/jobs');
      } else {
        navigate('/opportunity/chatbot', { state: { useResumeContext: true } });
      }
      return;
    }

    const data = new FormData();
    if (formData.file) {
      data.append("file", formData.file);
      const url = PARSE_RESUME;

      try {
        setSubmitError('');
        setIsParsing(true);
        const response = await makeAuthenticatedPostForFileResponse(url, data);
        if (response.status !== 200) {
          throw new Error(response.data?.message || "Resume parsing did not complete.");
        }

        localStorage.setItem("resume", "true");
        if (!isMountedRef.current) return;

        if(props.for === 'job') {
          navigate('/opportunity/uploadingResume/recommend');
        } else {
          navigate('/opportunity/chatbot', { state: { useResumeContext: true } });
        }
      } catch (error) {
        console.error("Error sending resume:", error);
        if (isMountedRef.current) {
          setSubmitError("We couldn’t process your resume. Please try again.");
        }
      } finally {
        if (isMountedRef.current) setIsParsing(false);
      }
    }
  };

  //use Effects

  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);

  useEffect(() => {
    const isNotDirty =
      (formData.file != null || formData.linkedIn !== '' || props.hasSavedResume) && !error;
    setIsDirty(!isNotDirty);
  }, [error, formData, props.hasSavedResume]);

  

  const handleInvalid = (event: any) => {
    console.log("invalid");
    setError('Please enter a valid LinkedIn profile URL.');
  };

  return (
    <>
      <form
        id="career-resume-form"
        className="doc-form upload-doc-form"
        onSubmit={handleSubmit}
        aria-busy={isParsing}
      >
        {isParsing && (
          <div
            className="resume-parsing-status"
            id="resume-parsing-status"
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            <span className="resume-parsing-spinner" aria-hidden="true" />
            <div>
              <h2>Parsing your resume</h2>
              <p>We’re extracting your skills, experience, and education.</p>
              <p>Keep this page open. Career Discovery chat will start automatically when parsing is complete.</p>
            </div>
          </div>
        )}
        {fileName!=='' && (
            <div role="status" aria-live="polite" className="d-flex w-[90%] doc-upload-alert mb-5">
              <p>Resume file selected: {fileName}</p>
            </div>
          )}
        <div className="mb-3 input-div">
          <h2 id="formFileLabel" className="form-label">
            {formData.file
              ? "File uploaded"
              : props.hasSavedResume
                ? "Upload a newer resume (optional)"
                : "Upload your resume"}
          </h2>
          {/* <p>Add your Audio/Video here</p> */}
          <section
            className={`upload-input-container ${dragging ? "is-dragging" : ""} ${isParsing ? "is-disabled" : ""}`}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <img src={uploadImg} alt="" aria-hidden="true" />
            <p style={{ fontSize: "1rem", color: "#000000" }}>
              Drag your file to start uploading
            </p>
            <img src={or} alt="" />
            <input
              className="form-control input-1"
              type="file"
              id="formFile"
              onChange={handleFileChange}
              name="file"
              accept=".pdf"
              aria-describedby="fileHelp"
              aria-labelledby="formFileLabel"
              required={!props.hasSavedResume}
              disabled={isParsing}
            />
            <button
              type="button"
              className="upload-button"
              onClick={() => document.getElementById("formFile")?.click()}
              disabled={isParsing}
            >
              {fileName === "" ? "Browse files" : "Change File"}
            </button>
            <p
              className="filename"
              style={{ fontSize: "0.75rem", padding: "0", margin: "0" }}
            >
              {fileName}
            </p>
          </section>
          <p id="fileHelp">
          Supported Input Formats: PDF. Max file size limit - 300 mb
          </p>
          {/* <p className="filename">{fileName}</p> */}
        </div>  
      </form>
      {error && <p className="page-error" role="alert">{error}</p>}
      {submitError && <p className="page-error" role="alert">{submitError}</p>}
      <div className="opportunity-form-actions">
      {props.for !=="job" && <button
          type="button"
          className="doc-submit doc-submit-skip px-2"
          onClick={handleSkip}
          disabled={isParsing}
        >
          Continue without a resume
        </button>}
        {(formData.file || props.hasSavedResume || isParsing) && <button
          type="submit"
          className="doc-submit"
          disabled={isDirty || isParsing}
          form="career-resume-form"
          aria-busy={isParsing}
          aria-describedby={isParsing ? "resume-parsing-status" : isDirty ? "career-submit-help" : undefined}
        >
          {isParsing
            ? "Parsing resume…"
            : formData.file
            ? "Continue with uploaded resume"
            : "Continue with saved resume"}
        </button>}
        <p id="career-submit-help" className="sr-only">
          Select a PDF resume to enable the submit button.
        </p>
      </div>
    















    {/* <form className="doc-form opportunity-form"  onSubmit={handleSubmit}>
      <ToastContainer
        position="top-right"
        autoClose={8000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable={false}
        pauseOnHover
        theme="light"
        transition={Bounce}
        role="alert"
        aria-live="polite"
        aria-atomic="true"
      />
      <ToastContainer />
      <div className="mb-3 input-div">
        <h2 id="formFileLabel" className="form-label">
          {formData.file === null?"Upload your resume":"File uploaded"}
        </h2>
        <p>{fileName}</p>
        <p>Supported Input Formats: PDF. Max file size limit - 300 mb</p>
        <input
          aria-label="upload file"
          className="form-control input-1"
          type="file"
          id="formFile"
          onChange={handleFileChange}
          onInvalid={handleInvalid}
          name="file"
          accept=".pdf"
          role="button"
          aria-labelledby="formFileLabel"
          disabled={formData.linkedIn !== ''}
        />
        <label htmlFor="formFile" className="upload-button" role="button">
          {fileName===''?'Upload File':'Change File'}
        </label>
      </div>
      {error && <p style={{ color: 'red' }}>{error}</p>}
      
      <footer className="form-footer" style={props.for==='job'?{justifyContent:"end"}:{}}>
      {props.for !=="job" && <button
          type="submit"
          className="btn btn-primary btn-center submit"
          onClick={handleSkip}
        >
          Skip
        </button>}
        <button
          type="submit"
          className="btn btn-primary btn-center submit"
          onClick={handleSubmit}
          disabled={isDirty}
        >
          Submit
        </button>
      </footer>
    </form> */}
    </>
  );
};

export default OpportunityForm;
